import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchOrders,
  updateOrder,
  createOrder,
  deleteOrder,
  fetchOrder,
} from "../api/order";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  order: {},
  totalCount: 0,
  currentCount: 0,
  newestOrdersCount: 0,
};

export const initialController = {
  filters: [],
  page: 1,
  per_page: 200,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteOrderAsync = createAsyncThunk(
  "orders/deleteOrderAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteOrder(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_order");
    }
  }
);

export const createOrderAsync = createAsyncThunk(
  "orders/createOrderAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createOrder(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_order");
      }
      return response;
    } catch (error) {
      return rejectNormalized(rejectWithValue, error, "create_order");
    }
  }
);

export const updateOrderAsync = createAsyncThunk(
  "orders/updateOrderAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateOrder(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_order");
      }
      return response;
    } catch (error) {
      return rejectNormalized(rejectWithValue, error, "update_order");
    }
  }
);

export const getOrdersAsync = createAsyncThunk(
  "orders/getOrdersAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchOrders(controller);
      const status = response?.response?.status ?? response?.status;

      if (
        isHttpFailureStatus(status) ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_orders");
      }

      if (!Array.isArray(response?.data)) {
        return rejectNormalized(rejectWithValue, response, "list_orders");
      }

      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_orders");
    }
  }
);

export const getOrderAsync = createAsyncThunk(
  "orders/getOrderAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchOrder(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_order");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "get_order");
    }
  }
);

const orderSlice = createSlice({
  name: "orders",
  initialState,
  reducers: { adoptOrderSnapshot(state, action) { state.order = action.payload; } },
  extraReducers: (builder) => {
    builder
      .addCase(getOrdersAsync.pending, (state, action) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
        // Clear data immediately on non-append fetches so the skeleton shows
        if (!action.meta?.arg?.append) {
          state.data = [];
          state.totalCount = 0;
        }
      })
      .addCase(getOrdersAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        const rows = action.payload?.data ?? [];
        if (action.meta?.arg?.append) {
          // Infinite scroll: concatenate new page to existing rows
          state.data = [...(state.data || []), ...rows];
        } else {
          state.data = rows;
        }
        state.totalCount = action.payload?.totalCount ?? 0;
        state.currentCount = state.data.length;
        state.newestOrdersCount = action.payload?.newestOrdersCount ?? 0;
      })
      .addCase(getOrdersAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente ordrene.");
      })
      .addCase(updateOrderAsync.pending, (state) => {
        state.isSaving = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateOrderAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.order = action.payload;
          draftState.isSaving = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(updateOrderAsync.rejected, (state, action) => {
        state.isSaving = false;
        state.error = false;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere ordren.");
      })
      .addCase(createOrderAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createOrderAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          if (action.payload?.id) {
            draftState.data.push(action.payload);
          }
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(createOrderAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette ordren.");
      })
      .addCase(deleteOrderAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(deleteOrderAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.meta?.arg;
          if (id != null && Array.isArray(draftState.data)) {
            draftState.data = draftState.data.filter((row) => row.id !== id);
          } else if (action.payload?.data) {
            draftState.data = action.payload.data;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(deleteOrderAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette ordren.");
      })
      .addCase(getOrderAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(getOrderAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.order = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getOrderAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente ordren.");
      });
  },
});

// export const {} = customerSlice.actions;

export const { reducer } = orderSlice;

export default orderSlice;

export const selectOrderById = (state, orderId) =>
  state.orders.data.find((order) => order.id == orderId);

export const { adoptOrderSnapshot } = orderSlice.actions;
